import { ref, watch, type Ref } from "vue";
import { systemVarsPool } from "@/utils/const";
import { extractInfoNodesData } from "@/flow/flow-util/node/utils";
import { t } from "@/utils/i18n";
import { JudgeType, Variable } from "@/types/conditionJudgeNodeType";

export const useEndReplyVariables = (
  xGraph: Ref<any> | undefined,
  varIdList: Ref<Array<{ id: string; nodeId: string; varName: string }>>,
  inputVarList: Ref<Array<{ name: string; type: string }>>,
) => {
  const variables = ref<Variable[]>([]);

  // 构建变量列表（供 xGraph watcher 和 inputVarList watcher 共用）
  const buildVariables = (graph: any) => {
    if (!graph?.getNodes) {
      variables.value = [];
      return;
    }

    const systemVars: Variable[] = systemVarsPool.map((v) => ({
      value: `\${${JudgeType.SYSTEM}-${v.value}}`,
      label: v.label,
      type: JudgeType.SYSTEM,
    }));

    const nodes = graph.getNodes();
    const infoVars: Variable[] = extractInfoNodesData(nodes)
      .map((v) => {
        const validChildren = v.infos
          .filter((info) => info.name && info.name.trim())
          .map((info) => ({
            value: `\${${JudgeType.INFO}-${info.id}}`,
            label: info.name,
            type: JudgeType.INFO,
            id: info.id,
          }));

        return {
          value: v.nodeId,
          type: JudgeType.INFO,
          label: v.nodeName,
          id: v.nodeId,
          children: validChildren,
        };
      })
      .filter((v) => (v.children?.length ?? 0) > 0);

    const apiCallNodesData: Array<{
      nodeId: string;
      nodeName: string;
      returns: Array<{ variable_id: string; key: string }>;
    }> = [];

    nodes.forEach((node) => {
      const nodeModel = node.getModel ? node.getModel() : node;
      const nodeData = nodeModel.nodeData;
      if (
        nodeData.nodeType === "interfaceInvocationNode" &&
        nodeData.returns &&
        Array.isArray(nodeData.returns)
      ) {
        apiCallNodesData.push({
          nodeId: nodeModel.id || nodeData.id,
          nodeName: nodeData.nodeName || "",
          returns: nodeData.returns.filter(
            (ret) =>
              ret.variable_id &&
              ret.key &&
              ret.variable_id.startsWith("userdefine-"),
          ),
        });
      }
    });

    const apiCallVars: Variable[] = apiCallNodesData
      .map((node) => ({
        value: node.nodeId,
        type: JudgeType.CUSTOM,
        label: node.nodeName,
        id: node.nodeId,
        children: node.returns.map((ret) => {
          const varInfo = varIdList.value.find((v) => v.id === ret.variable_id);
          return {
            value: `\${${JudgeType.CUSTOM}-${ret.variable_id}}`,
            label: varInfo ? varInfo.varName : ret.key,
            type: JudgeType.CUSTOM,
            id: ret.variable_id,
          };
        }),
      }))
      .filter((v) => (v.children?.length ?? 0) > 0);

    // 从启动节点提取 inputVariables
    const inputVars: Variable[] = [];
    nodes.forEach((node) => {
      const nodeModel = node.getModel ? node.getModel() : node;
      const nodeData = nodeModel.nodeData;
      if (
        nodeData.nodeType === "userIntentNode" &&
        nodeData.inputVariables &&
        Array.isArray(nodeData.inputVariables)
      ) {
        const children = nodeData.inputVariables
          .filter((v: { name: string }) => v.name && v.name.trim())
          .map((v: { name: string }) => ({
            value: `\${${JudgeType.INPUT}-${v.name}}`,
            label: v.name,
            type: JudgeType.INPUT,
            id: v.name,
          }));
        if (children.length > 0) {
          inputVars.push(...children);
        }
      }
    });

    variables.value = [
      {
        value: JudgeType.SYSTEM,
        label: t("系统变量"),
        type: JudgeType.SYSTEM,
        children: systemVars,
      },
      ...infoVars,
      ...apiCallVars,
      ...(inputVars.length > 0
        ? [
            {
              value: JudgeType.INPUT,
              label: t("启动节点变量"),
              type: JudgeType.INPUT,
              children: inputVars,
            },
          ]
        : []),
    ];
  };

  watch(
    xGraph as any,
    (newVal) => {
      buildVariables(newVal);
    },
    { immediate: true },
  );

  // 监听 inputVarList 变化，重建变量列表以触发下游清理
  watch(
    inputVarList,
    () => {
      buildVariables((xGraph as any)?.value);
    },
    { deep: true },
  );

  return {
    variables,
  };
};
